import { useEffect, useState } from 'react'
import { useAuth } from '@praxis/component-auth'
import { useEnv } from '@praxis/component-runtime-env'
import {
  PARAM_RETURNURL,
  PARAM_SUPPLIER_ID,
  PARAM_LOCATION_ID,
  PARAM_MANUFACTURING_TYPE_ID,
  RFI_TYPE_LOCATION,
  RFI_TYPE_SUPPLIER,
  ADMIN_ID_CHATBOT_LCM,
  ADMIN_CONTROL_TYPE,
} from '../common/commonConstants'
import {
  Accordion,
  Card,
  List,
  Spinner,
  Button,
  Input,
} from '@design-systems/canvas-ui'
import Icon, {
  ErrorIcon,
  ProfileIcon,
  LocationIcon,
} from '@design-systems/react-icons'
import { Link, useSearchParams } from 'react-router-dom'
import { getRfiMenuOptions } from '../services/lcmService'
import { isEmpty } from 'lodash'
import ChatBotWidget from './Chatbot/ChatBotWidget'
import { getControlVisibility } from '../common'

/**
 * Left side menu for RFI Pages
 * @param {*} props
 * @returns
 */
export const SideNavigation = (props) => {
  const auth = useAuth()
  const env = useEnv()
  const userId = auth.session.userInfo.lanId

  const [menuOptions, setMenuOptions] = useState(null)
  const [isMenuFetchInProgress, setIsMenuFetchInProgress] = useState(true)
  const [isMenuFetchFailed, setIsMenuFetchFailed] = useState(false)
  const [openSections, setOpenSections] = useState({})
  const [openSubMenus, setOpenSubMenus] = useState({})
  const [searchParams] = useSearchParams()

  const manufacturingTypeId =
    searchParams.get(PARAM_MANUFACTURING_TYPE_ID) ||
    env.mfelcm?.appConstants?.defaultManufacturingTypeId //NOTE:Supporting existing shared/bookmarked URLs that dont have manufType; defaulting to 8 (finishedGood) as that was only capability available till now
  let supplierId = searchParams.get(PARAM_SUPPLIER_ID)
  let locationId = searchParams.get(PARAM_LOCATION_ID)

  // To Check whether the URL contains location_id or not, if doesn't contain then its RFI1
  let checkNotRFI2 = !isEmpty(locationId)

  useEffect(() => {
    getMenuOptions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!menuOptions?.sections?.length) {
      return
    }

    setOpenSections((prevOpenSections) => {
      if (Object.keys(prevOpenSections).length) {
        return prevOpenSections
      }

      return menuOptions.sections.reduce((acc, eachSection, sectionIndex) => {
        acc[getSectionKey(eachSection, sectionIndex)] = true
        return acc
      }, {})
    })

    setOpenSubMenus((prevOpenSubMenus) => {
      if (Object.keys(prevOpenSubMenus).length) {
        return prevOpenSubMenus
      }

      return menuOptions.sections.reduce((sectionAcc, eachSection) => {
        eachSection.rfimenu_sub_sections?.forEach(
          (eachSubMenuItem, eachSubMenuItemIndex) => {
            sectionAcc[getSubMenuKey(eachSubMenuItem, eachSubMenuItemIndex)] =
              eachSubMenuItem?.sub_section_id === locationId
          },
        )
        return sectionAcc
      }, {})
    })
  }, [locationId, menuOptions])

  /**
   * GET the left menu options for RFI internal/external page
   * Contains
   * 1. Supplier Details - finished good, fabric, trim etc
   * 2. Location Details - all locations with finished good, fabric, trim etc
   */
  const getMenuOptions = async () => {
    setIsMenuFetchInProgress(true)
    setIsMenuFetchFailed(false)
    try {
      let menuData = await getRfiMenuOptions(
        props.isReadonly ? supplierId : userId,
        props.isReadonly,
      )
      setMenuOptions(menuData)
      setIsMenuFetchInProgress(false)
      setIsMenuFetchFailed(false)
    } catch (error) {
      setIsMenuFetchInProgress(false)
      setIsMenuFetchFailed(true)
    }
  }

  /**
   * Get the menu icon
   * @param {*} key
   * @returns
   */
  const getIcon = (key) => {
    switch (key.toUpperCase()) {
      case 'SUPPLIERDETAILS':
        return ProfileIcon
      case 'LOCATIONDETAILS':
        return LocationIcon
      default:
        return undefined
    }
  }

  const getSectionKey = (eachSection, sectionIndex) => {
    return `${eachSection.section_group || 'section'}-${sectionIndex}`
  }

  const getSubMenuKey = (eachSubMenuItem, eachSubMenuItemIndex) => {
    return `${eachSubMenuItem.sub_section_id || 'submenu'}-${eachSubMenuItemIndex}`
  }

  const toggleSection = (sectionKey) => {
    setOpenSections((prevOpenSections) => ({
      ...prevOpenSections,
      [sectionKey]: !(prevOpenSections[sectionKey] ?? true),
    }))
  }

  const toggleSubMenu = (subMenuKey) => {
    setOpenSubMenus((prevOpenSubMenus) => ({
      ...prevOpenSubMenus,
      [subMenuKey]: !(prevOpenSubMenus[subMenuKey] ?? false),
    }))
  }

  /**
   * Build left side menu items under 2 categories - Supplier Details, Location Details
   * @returns
   */
  const buildMenu = () => {
    return menuOptions?.sections?.map((eachSection, sectionIndex) => {
      const sectionKey = getSectionKey(eachSection, sectionIndex)
      const isLocationSection =
        eachSection.section_group?.toUpperCase() === 'LOCATIONDETAILS'

      return (
        <Accordion
          header={eachSection.section_display_name}
          icon={getIcon(eachSection.section_group)}
          spacing="dense"
          preserveDom
          isOpen={openSections[sectionKey] ?? true}
          onChange={() => toggleSection(sectionKey)}
          key={`sidenav-menu-${sectionIndex}`}
          data-testid={`sidenav-menu-${sectionIndex}`}
          className={`sidenav-menu ${
            isLocationSection
              ? 'sidenav-menu--locations'
              : 'sidenav-menu--supplier'
          }`}
        >
          {eachSection?.section_items && (
            <List
              className="sidenav-list hc8-ma-none hc8-pa-none"
              id="side-nav-list-items"
            >
              {eachSection?.section_items?.map(
                (eachSectionItem, eachSectionItemIndex) => {
                  return (
                    <List.Item
                      className="sidenav-list-item"
                      key={`sidenav-li-${eachSectionItemIndex}`}
                      id={`sidenav-li-${eachSectionItemIndex}`}
                      isSelected={
                        eachSectionItem.manufacturing_type_id ===
                          Number(manufacturingTypeId) && !checkNotRFI2
                      }
                      as={Link}
                      to={getRfiDetailUrl(
                        RFI_TYPE_SUPPLIER,
                        eachSectionItem.manufacturing_type_id,
                      )}
                    >
                      <span className="hc8-ml-2x">
                        {eachSectionItem.display_name}
                      </span>
                    </List.Item>
                  )
                },
              )}
            </List>
          )}
          {eachSection.rfimenu_sub_sections &&
            eachSection.rfimenu_sub_sections?.map(
              (eachSubMenuItem, eachSubMenuItemIndex) => {
                const subMenuKey = getSubMenuKey(
                  eachSubMenuItem,
                  eachSubMenuItemIndex,
                )

                return (
                  <Accordion
                    header={eachSubMenuItem.sub_section_name}
                    spacing="dense"
                    preserveDom
                    isOpen={
                      openSubMenus[subMenuKey] ??
                      eachSubMenuItem?.sub_section_id === locationId
                    }
                    onChange={() => toggleSubMenu(subMenuKey)}
                    key={`sidenav-submenu-${eachSubMenuItemIndex}`}
                    data-testid={`sidenav-submenu-${eachSubMenuItemIndex}`}
                    className="sidenav-submenu sidenav-location-item"
                  >
                    {eachSubMenuItem.sub_section_items && (
                      <List
                        className="sidenav-list hc8-ma-none hc8-pa-none"
                        id="side-nav-list-items"
                      >
                        {eachSubMenuItem.sub_section_items.map(
                          (
                            eachSubSectionMenuItem,
                            eachSubSectionMenuItemIndex,
                          ) => {
                            return (
                              <List.Item
                                key={`sidenav-submenu-li-${eachSubMenuItemIndex}-${eachSubSectionMenuItemIndex}`}
                                id={`sidenav-submenu-li-${eachSubMenuItemIndex}-${eachSubSectionMenuItemIndex}`}
                                isSelected={
                                  eachSubSectionMenuItem.manufacturing_type_id ===
                                    Number(manufacturingTypeId) &&
                                  locationId === eachSubMenuItem.sub_section_id
                                }
                                as={Link}
                                to={getRfiDetailUrl(
                                  RFI_TYPE_LOCATION,
                                  eachSubSectionMenuItem.manufacturing_type_id,
                                  eachSubMenuItem.sub_section_id,
                                )}
                                className="sidenav-list-item"
                              >
                                <span className="hc8-ml-2x">
                                  {eachSubSectionMenuItem.display_name}
                                </span>
                              </List.Item>
                            )
                          },
                        )}
                      </List>
                    )}
                  </Accordion>
                )
              },
            )}
        </Accordion>
      )
    })
  }

  /**
   * Form the RFI Page detail URL for redirecting when clicked on left menu
   * Can redirect to RFI1 - internal, external page
   * OR RFI2 - internal, external page
   * @param {*} rfiType
   * @param {*} manufacturingTypeId
   * @param {*} locationId
   * @returns
   */
  const getRfiDetailUrl = (rfiType, manufacturingTypeId, locationId = null) => {
    let rfiDetailUrl
    if (rfiType === RFI_TYPE_LOCATION) {
      rfiDetailUrl = props.isReadonly
        ? `/internal/rfi2?${PARAM_SUPPLIER_ID}=${supplierId}`
        : `/rfi2?${PARAM_RETURNURL}=${props.returnUrl}`
      rfiDetailUrl = `${rfiDetailUrl}&${PARAM_LOCATION_ID}=${locationId}`
    } else {
      rfiDetailUrl = props.isReadonly
        ? `/internal/rfi1?${PARAM_SUPPLIER_ID}=${supplierId}`
        : `/rfi1?${PARAM_RETURNURL}=${props.returnUrl}`
    }
    return `${rfiDetailUrl}&${PARAM_MANUFACTURING_TYPE_ID}=${manufacturingTypeId}`
  }

  return (
    <div className="rfi_scroll sidenav-scroll">
      {/* 1. In Progress */}
      {isMenuFetchInProgress && !isMenuFetchFailed && (
        <div className="height-100 align-centre-centre">
          <Spinner size="lg" data-testid="spinner-left-menu" />
        </div>
      )}

      {/* 2. Error */}
      {!isMenuFetchInProgress && isMenuFetchFailed && (
        <div className="height-100 align-centre-centre">
          <Button
            onClick={() => getMenuOptions()}
            data-testid="btn-left-menu-error"
            variant="ghost"
            isIconOnly
            size="lg"
            aria-label="click to open left menu option"
          >
            <Icon svg={ErrorIcon} className="hc8-clr-error icon-large" />
          </Button>
        </div>
      )}

      {/* 3. Menu Items */}
      {!isMenuFetchInProgress && !isMenuFetchFailed && (
        <>
          <Card
            variant="default"
            className="sidenav-supplier-header hc8-ph-2x hc8-pt-2x hc8-pb-2x"
            data-testid="sideNavLogoHeader"
          >
            <Input.Label className="hc8-fs-300">
              <strong>{menuOptions.supplier_name}</strong>
            </Input.Label>
          </Card>

          {buildMenu()}
        </>
      )}

      {/* 4. Chatbot */}
      {props.isReadonly &&
        getControlVisibility(
          ADMIN_ID_CHATBOT_LCM,
          ADMIN_CONTROL_TYPE,
          props.userSettings,
        ) && <ChatBotWidget />}
    </div>
  )
}
